import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

interface Props {
    /**
     * The custom color passed to the active tab.
     */
    activeTabColor?: string;
    /**
     * The active tab identifier.
     */
    activeTab: string;
    /**
     * The list of tabs to render.
     */
    tabs: Array<{
        id: string;
        label: string;
        testId: string | null | undefined;
    }>;
    /**
     * Called when a tab is selected.
     */
    onTabChange: (id: string) => void;
}

/**
 * Tab Filter allows to select any of the filters associated to a table and uses
 * that info to execute a particular action. This action can be filtering out
 * the results of the current table or navigating to a different page. A
 * selection is always required.
 */
const FilterTabs = (props: Props) => {
    const {
        activeTab,
        tabs,
        onTabChange,
        activeTabColor = semanticColor.core.foreground.instructive.default,
    } = props;

    return (
        <View role="tablist" style={styles.tabList}>
            {tabs.map((tab) => {
                const selected = tab.id === activeTab;
                return (
                    <Clickable
                        key={tab.id}
                        hideDefaultFocusRing={true}
                        onClick={() => onTabChange(tab.id)}
                        aria-selected={selected ? "true" : "false"}
                        role="tab"
                        testId={tab.testId || ""}
                    >
                        {({hovered, focused, pressed}) => (
                            <View
                                style={[
                                    styles.tab,
                                    hovered && styles.hovered,
                                    focused && styles.focused,
                                    pressed && styles.pressed,
                                ]}
                            >
                                <LabelMedium
                                    style={[
                                        styles.tabLabel,
                                        selected && {
                                            color: activeTabColor,
                                            borderBottomColor: activeTabColor,
                                        },
                                    ]}
                                >
                                    {tab.label}
                                </LabelMedium>
                            </View>
                        )}
                    </Clickable>
                );
            })}
        </View>
    );
};

const styles = StyleSheet.create({
    tabList: {
        flexDirection: "row",
    },
    tab: {
        borderRadius: spacing.xxxxSmall_2,
        padding: `0 ${spacing.xSmall_8}px`,
    },
    focused: {
        boxShadow: `0 0 0 ${spacing.xxxxSmall_2}px ${semanticColor.focus.outer}`,
    },
    hovered: {
        backgroundColor: semanticColor.core.background.instructive.subtle,
    },
    pressed: {
        backgroundColor: semanticColor.core.background.base.strong,
    },
    tabLabel: {
        borderBlockEnd: "3px solid transparent",
        color: "inherit",
        fontWeight: 700,
        padding: `${spacing.xSmall_8}px 0`,
    },
});

export default FilterTabs;
